import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FloatingAIChat from '../../components/ui/FloatingAIChat';
import CourseHero from '../course-detail-view/components/CourseHero';
import ModuleCard from '../course-detail-view/components/ModuleCard';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useCourseWithModulesAndLessons } from '../../hooks/useCourseData';
import { getInstructorForCourse } from '../../data/instructors';
import { useAuth } from '../../contexts/AuthContext';
import { useCourseAccess } from '../../hooks/useCourseAccess';
import { isPrivilegedContentRole } from '@/lib/courseAccess';
import { useUserProgress } from '../../hooks/useUserProgress';

const CourseModulesView = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { user, session } = useAuth();
  const courseSlug = params.courseSlug || 'formacao-lideres-mentores';
  
  const { tenantId, course, modules, loading: courseLoading } = useCourseWithModulesAndLessons(courseSlug);
  const courseIdString = course?.id ? String(course.id) : undefined;
  const tenantRole = session?.userTenant?.role;
  const { isFullCourseEnrolled, isModuleAccessible } = useCourseAccess(
    session?.systemUser?.id,
    courseIdString
  );
  const isPrivileged = isPrivilegedContentRole(tenantRole);
  const { progress } = useUserProgress(session?.systemUser?.id, user?.id, courseIdString);
  
  // Preparar dados do curso para CourseHero
  const courseData = {
    id: course?.id || "1a1f5462-fa9c-4e94-a067-2cdd7168a8d4",
    slug: course?.slug || courseSlug,
    title: course?.title || "Carregando...",
    category: "Desenvolvimento",
    description: course?.summary || "Carregando descrição do curso...",
    fullDescription: course?.summary || "Carregando descrição completa...",
    thumbnail: course?.cover || "/assets/images/no_image.png",
    duration: null,
    studentsCount: null,
    rating: 0,
    lessonsCount: modules?.reduce((total, module) => total + (module?.lessons?.length || 0), 0) || 0,
    certificateAvailable: false,
    instructor: getInstructorForCourse(course?.instructor),
    learningObjectives: [
      "Fundamentos práticos para construir lideranca, posicionamento e mentorias de alto nivel de entrega e impacto.",
    ],
    requirements: [
      "Disciplina, Foco e Persistencia para construir grandes resultados",
    ],
    modules: modules || []
  };

  const userProgress = progress ? {
    completedLessons: progress.completedLessonIds,
    moduleProgress: progress.moduleProgress,
    progressPercentage: progress.progressPercentage,
    totalLessons: progress.totalLessons,
  } : null;

  const breadcrumbItems = [
    { label: 'Início', path: '/dashboard-home' },
    { label: 'Cursos', path: '/courses' },
    { label: courseData?.title, path: `/course/${courseSlug}` },
    { label: 'Módulos', path: `/course/${courseSlug}/modules`, isActive: true }
  ];

  const handleModuleClick = (module) => {
    if (!isPrivileged && !isFullCourseEnrolled && !isModuleAccessible(module?.id)) {
      return;
    }
    if (courseSlug && module?.slug) {
      navigate(`/course/${courseSlug}/module/${module.slug}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb customItems={breadcrumbItems} />
          
          <div className="flex flex-col">
            {/* Main Content */}
            <div className="flex-1">
              <CourseHero 
                course={courseData} 
                userProgress={userProgress} 
                modules={modules} 
                tenantId={tenantId} 
              />
              
              {/* Header da Seção */}
              <div className="mb-6">
                <h2 className="text-2xl font-heading font-semibold text-foreground mb-2">
                  Módulos do Curso
                </h2>
                <p className="text-muted-foreground">
                  Explore todos os módulos disponíveis neste curso
                </p>
              </div>

              {/* Grid de Módulos */}
              {courseLoading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-muted rounded-lg h-64 animate-pulse"></div>
                  ))}
                </div>
              ) : modules && modules.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {modules.map((module) => (
                    <ModuleCard
                      key={module.id}
                      module={module}
                      courseSlug={courseSlug}
                      onClick={handleModuleClick}
                      isLocked={
                        !isPrivileged &&
                        !isFullCourseEnrolled &&
                        !isModuleAccessible(module?.id)
                      }
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="Layers" size={24} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Nenhum módulo disponível</h3>
                  <p className="text-muted-foreground mb-4">
                    Este curso ainda não possui módulos cadastrados.
                  </p>
                  <Button onClick={() => navigate('/courses')}>
                    Voltar ao Catálogo
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <FloatingAIChat />
    </div>
  );
};

export default CourseModulesView;
